import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Button,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Skeleton,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CodeIcon from "@mui/icons-material/Code";
import RefreshIcon from "@mui/icons-material/Refresh";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import CreateSquarespaceCustomerDialog from "../components/admin/CreateSquarespaceCustomerDialog";
import OrderSessionDialog from "../components/admin/OrderSessionDialog";
import JsonDataModal from "../components/admin/JsonDataModal";
import SquarespacePageHeader from "../components/SquarespacePageHeader";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useSnackbar } from "../hooks/useSnackbar.js";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8787";

export default function SquarespaceCustomers() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { showSuccess, showError, SnackbarComponent } = useSnackbar();
  const [customersData, setCustomersData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [pagination, setPagination] = React.useState({
    page: 1,
    limit: 50,
    total: 0,
    offset: 0,
  });
  const [paginationUrls, setPaginationUrls] = React.useState({
    nextPageUrl: null,
    previousPageUrl: null,
  });
  const [pageHistory, setPageHistory] = React.useState([]);
  
  // Create customer modal state
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [createFormData, setCreateFormData] = React.useState({
    email: "",
    firstName: "",
    lastName: "",
    metadataCountry: "US",
    metadataIpAddress: "0.0.0.0",
    metadataUserAgent: "BryteBridge Admin",
    timezone: "America/New_York",
  });
  const [creatingCustomer, setCreatingCustomer] = React.useState(false);
  const [createError, setCreateError] = React.useState(null);
  const [createApiResponse, setCreateApiResponse] = React.useState(null);

  // Create Custom Order modal state (create by email when unable to create customer account)
  const [orderSessionDialogOpen, setOrderSessionDialogOpen] = React.useState(false);
  const [orderSessionFormData, setOrderSessionFormData] = React.useState({
    email: "",
    firstName: "",
    lastName: "",
    pricingId: "",
    title: "",
    domain: "",
  });
  const [orderSessionCreating, setOrderSessionCreating] = React.useState(false);
  const [orderSessionError, setOrderSessionError] = React.useState(null);
  const [orderSessionApiResponse, setOrderSessionApiResponse] = React.useState(null);

  // JSON debug modal state
  const [jsonModalOpen, setJsonModalOpen] = React.useState(false);

  const fetchCustomers = React.useCallback(async (nextPageUrlParam = null) => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      
      // Build the API URL with nextPageUrl as a query parameter
      const baseUrl = `${API_BASE_URL}/admin/squarespace/customers`;
      const fetchUrl = nextPageUrlParam 
        ? `${baseUrl}?nextPageUrl=${encodeURIComponent(nextPageUrlParam)}`
        : baseUrl;

      const response = await fetch(fetchUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to fetch customers: ${response.status} ${response.statusText}`
        );
      }

      const responseData = await response.json();
      // API now returns { data: [...], pagination: {...}, nextPageUrl: ... }
      const data = responseData.data || [];
      const paginationInfo = responseData.pagination || {};
      
      setCustomersData({
        data: data,
        pagination: paginationInfo,
      });
      
      // Update pagination URLs - check both root level and pagination object
      setPaginationUrls({
        nextPageUrl: responseData.nextPageUrl || paginationInfo.nextPageUrl || null,
        previousPageUrl: responseData.previousPageUrl || paginationInfo.previousPageUrl || null,
      });
      
      // Track page history for back navigation
      if (nextPageUrlParam && !pageHistory.includes(nextPageUrlParam)) {
        setPageHistory(prev => [...prev, nextPageUrlParam]);
      }
      
      // Update pagination state if pagination info exists
      if (paginationInfo) {
        setPagination({
          page: paginationInfo.page || 1,
          limit: paginationInfo.limit || data.length,
          total: paginationInfo.total || data.length,
          offset: paginationInfo.offset || 0,
        });
      } else {
        // If no pagination info, assume all data is shown
        setPagination({
          page: 1,
          limit: data.length,
          total: data.length,
          offset: 0,
        });
      }
    } catch (err) {
      console.error("Error fetching customers:", err);
      setError(err.message || "Failed to fetch customers");
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  React.useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleNextPage = () => {
    if (paginationUrls.nextPageUrl) {
      fetchCustomers(paginationUrls.nextPageUrl);
    }
  };

  const handlePreviousPage = () => {
    if (paginationUrls.previousPageUrl) {
      fetchCustomers(paginationUrls.previousPageUrl);
    } else if (pageHistory.length > 0) {
      // Go back in history
      const previousUrl = pageHistory[pageHistory.length - 1];
      setPageHistory(prev => prev.slice(0, -1));
      fetchCustomers(previousUrl);
    } else {
      // Go back to first page (no nextPageUrl parameter)
      fetchCustomers(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // Extract customers from data array
  const allCustomers = Array.isArray(customersData?.data) ? customersData.data : [];
  
  // Filter by search query
  const customers = React.useMemo(() => {
    if (!searchQuery.trim()) return allCustomers;
    
    const query = searchQuery.toLowerCase().trim();
    return allCustomers.filter((customer) => {
      const name = customer.name || '';
      const email = customer.email || '';
      const id = customer.customerId || customer.id || '';
      const searchableText = `${name} ${email} ${id}`.toLowerCase();
      return searchableText.includes(query);
    });
  }, [allCustomers, searchQuery]);

  // Handle create customer
  const handleCreateCustomer = async () => {
    if (!createFormData.email || !createFormData.email.trim()) {
      setCreateError("Email is required");
      return;
    }
    if (!createFormData.firstName?.trim()) {
      setCreateError("First name is required");
      return;
    }
    if (!createFormData.lastName?.trim()) {
      setCreateError("Last name is required");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(createFormData.email.trim())) {
      setCreateError("Please enter a valid email address");
      return;
    }

    setCreatingCustomer(true);
    setCreateError(null);

    try {
      const token = await getAccessTokenSilently().catch(() => null);
      
      // Prepare request body - email, firstName, lastName required; metadata for Squarespace
      const requestBody = {
        email: createFormData.email.trim(),
        firstName: createFormData.firstName.trim(),
        lastName: createFormData.lastName.trim(),
        metadata: {
          country: createFormData.metadataCountry?.trim() || "US",
          ipAddress: createFormData.metadataIpAddress?.trim() || "0.0.0.0",
          userAgent: createFormData.metadataUserAgent?.trim() || "BryteBridge Admin",
        },
        ...(createFormData.timezone?.trim() && { timezone: createFormData.timezone.trim() }),
      };

      const response = await fetch(`${API_BASE_URL}/admin/squarespace/customers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch {
          errorData = { error: "Failed to create customer", message: `${response.status} ${response.statusText}` };
        }
        const msg = errorData.message || errorData.error || "Failed to create customer";
        setCreateApiResponse(errorData);
        setCreateError(msg);
        showError(msg);
        return;
      }

      const successData = await response.json();
      setCreateApiResponse(successData);
      setCreateError(null);
      showSuccess("Customer created successfully");
    } catch (err) {
      console.error("Error creating customer:", err);
      const msg = err.message || "Failed to create customer";
      const errPayload = { error: "Request failed", message: msg };
      setCreateApiResponse(errPayload);
      setCreateError(msg);
      showError(msg);
    } finally {
      setCreatingCustomer(false);
    }
  };

  const handleCreateDialogClose = () => {
    setCreateDialogOpen(false);
    setCreateFormData({
      email: "",
      firstName: "",
      lastName: "",
      metadataCountry: "US",
      metadataIpAddress: "0.0.0.0",
      metadataUserAgent: "BryteBridge Admin",
      timezone: "America/New_York",
    });
    setCreateError(null);
    setCreateApiResponse(null);
    if (createApiResponse?.success) {
      fetchCustomers();
    }
  };

  const handleCreateOrderSession = async () => {
    if (!orderSessionFormData.email?.trim()) {
      setOrderSessionError("Email is required");
      return;
    }
    if (!orderSessionFormData.firstName?.trim() || !orderSessionFormData.lastName?.trim()) {
      setOrderSessionError("First name and last name are required");
      return;
    }
    if (!orderSessionFormData.pricingId || !orderSessionFormData.pricingId.trim()) {
      setOrderSessionError("Pricing ID is required");
      return;
    }
    if (!orderSessionFormData.title || !orderSessionFormData.title.trim()) {
      setOrderSessionError("Website title is required");
      return;
    }

    setOrderSessionCreating(true);
    setOrderSessionError(null);
    setOrderSessionApiResponse(null);

    try {
      const token = await getAccessTokenSilently().catch(() => null);
      const newOrderItemWebSite = {
        pricingId: orderSessionFormData.pricingId.trim(),
        title: orderSessionFormData.title.trim(),
      };
      if (orderSessionFormData.domain?.trim()) {
        newOrderItemWebSite.domains = [orderSessionFormData.domain.trim()];
      }

      const response = await fetch(`${API_BASE_URL}/admin/squarespace/orders/by-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          email: orderSessionFormData.email.trim(),
          firstName: orderSessionFormData.firstName.trim(),
          lastName: orderSessionFormData.lastName.trim(),
          newOrderItemWebSite,
        }),
      });

      const responseData = await response.json();

      if (!response.ok) {
        const msg = responseData.message || responseData.error || "Failed to create order";
        setOrderSessionApiResponse(responseData);
        setOrderSessionError(msg);
        showError(msg);
        return;
      }

      setOrderSessionApiResponse({ success: true, data: responseData.data || responseData });
      setOrderSessionError(null);
      showSuccess("Order created successfully");
      setOrderSessionFormData({
        email: "",
        firstName: "",
        lastName: "",
        pricingId: "",
        title: "",
        domain: "",
      });
    } catch (err) {
      console.error("Error creating custom order:", err);
      const msg = err.message || "Failed to create order";
      setOrderSessionApiResponse({ error: "Request failed", message: msg });
      setOrderSessionError(msg);
      showError(msg);
    } finally {
      setOrderSessionCreating(false);
    }
  };

  const handleOrderSessionDialogClose = () => {
    const wasSuccess = orderSessionApiResponse?.success || !!orderSessionApiResponse?.data;
    setOrderSessionDialogOpen(false);
    setOrderSessionFormData({
      email: "",
      firstName: "",
      lastName: "",
      pricingId: "",
      title: "",
      domain: "",
    });
    setOrderSessionError(null);
    setOrderSessionApiResponse(null);
    if (wasSuccess) {
      fetchCustomers();
    }
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Home
                </Typography>
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard/squarespace"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Squarespace
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  Customers
                </Typography>
              </Breadcrumbs>
            </Box>

            <SquarespacePageHeader
              title="Squarespace Customers"
              description="View and manage Squarespace customers"
              icon={<PeopleIcon sx={{ color: "primary.main" }} />}
            >
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => fetchCustomers()}
                disabled={loading}
                sx={{ mr: 1 }}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                startIcon={<CodeIcon />}
                onClick={() => setJsonModalOpen(true)}
                sx={{ mr: 2 }}
              >
                JSON
              </Button>
              {!loading && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Page:
                  </Typography>
                  <Chip
                    label={pagination.page || 1}
                    color="primary"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              )}
            </SquarespacePageHeader>

            {/* Create Customer / Create Custom Order panel */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" component="div" sx={{ mb: 2 }}>
                  The process for creating a Squarespace website starts with a customer, then goes to an order. The order is auto-fulfilled by Squarespace and results in a record that shows up in{" "}
                  <Link
                    to="/dashboard/squarespace/subscriptions"
                    style={{ color: "inherit", textDecoration: "underline" }}
                  >
                    Subscriptions
                  </Link>
                  . You need to create both the customer and the order.
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Box sx={{ flex: "1 1 0", minWidth: 280 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<PersonAddIcon />}
                      onClick={() => {
                        setCreateDialogOpen(true);
                        setCreateError(null);
                        setCreateApiResponse(null);
                      }}
                      sx={{ mb: 1 }}
                    >
                      Create Customer
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      Use when you don't already have a Squarespace customer ID. Creates a new customer account in Squarespace (email, name, metadata). Use this first for new contacts.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      Create the customer first, then create an order for them.
                    </Typography>
                  </Box>
                  <Box sx={{ flex: "1 1 0", minWidth: 280 }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<ShoppingCartIcon />}
                      onClick={() => {
                        setOrderSessionDialogOpen(true);
                        setOrderSessionError(null);
                      }}
                      sx={{ mb: 1 }}
                    >
                      Create Custom Order
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      Use when the person's email is already used in Squarespace (Create Customer would fail), or you want to create an order directly with raw customer information. Creates an order by email without requiring a customer ID first.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      Use when Create Customer fails (email in use) or to add an order for an existing customer.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : (
              <Card>
                <CardContent>
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      placeholder="Search by name, email, or customer ID..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  {/* Pagination controls below filters and above table */}
                  {(paginationUrls.nextPageUrl || paginationUrls.previousPageUrl || pageHistory.length > 0) && !loading && (
                    <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Button
                        variant="outlined"
                        onClick={handlePreviousPage}
                        disabled={!paginationUrls.previousPageUrl && pageHistory.length === 0 || loading}
                      >
                        Previous Page
                      </Button>
                      <Typography variant="body2" color="text.secondary">
                        Page {pagination.page || 1}
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={handleNextPage}
                        disabled={!paginationUrls.nextPageUrl || loading}
                      >
                        Next Page
                      </Button>
                    </Box>
                  )}

                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Customer ID</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Created</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading ? (
                          Array.from({ length: 10 }).map((_, index) => (
                            <TableRow key={`skeleton-${index}`}>
                              <TableCell>
                                <Skeleton height={20} />
                              </TableCell>
                              <TableCell>
                                <Skeleton height={20} />
                              </TableCell>
                              <TableCell>
                                <Skeleton height={20} />
                              </TableCell>
                              <TableCell>
                                <Skeleton height={20} width={100} />
                              </TableCell>
                              <TableCell>
                                <Skeleton height={24} width={80} borderRadius={4} />
                              </TableCell>
                              <TableCell align="right">
                                <Skeleton height={32} width={80} />
                              </TableCell>
                            </TableRow>
                          ))
                        ) : customers.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              <Typography color="text.secondary">
                                {searchQuery.trim() ? 'No customers found matching your search' : 'No customers available'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          customers.map((customer) => (
                            <TableRow key={customer.id || customer.customerId} hover>
                              <TableCell>
                                <Typography variant="body2" fontFamily="monospace">
                                  {customer.customerId || customer.id || 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>
                                  {customer.name || 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell>{customer.email || 'N/A'}</TableCell>
                              <TableCell>{formatDate(customer.createdOn || customer.created_at || customer.createdAt)}</TableCell>
                              <TableCell>
                                <Chip
                                  label={customer.status === "active" ? "Active" : customer.status || "N/A"}
                                  color={customer.status === "active" ? "success" : "default"}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<VisibilityIcon />}
                                  onClick={() => navigate(`/dashboard/squarespace/customers/${customer.customerId || customer.id}`)}
                                >
                                  View
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Pagination controls below table */}
                  {(paginationUrls.nextPageUrl || paginationUrls.previousPageUrl || pageHistory.length > 0) && !loading && (
                    <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Button
                        variant="outlined"
                        onClick={handlePreviousPage}
                        disabled={!paginationUrls.previousPageUrl && pageHistory.length === 0 || loading}
                      >
                        Previous Page
                      </Button>
                      <Typography variant="body2" color="text.secondary">
                        Page {pagination.page || 1}
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={handleNextPage}
                        disabled={!paginationUrls.nextPageUrl || loading}
                      >
                        Next Page
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {!loading && (
              <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  {searchQuery.trim() ? (
                    <>Showing {customers.length} customer{customers.length !== 1 ? 's' : ''} (filtered from {allCustomers.length} total)</>
                  ) : (
                    <>Showing {pagination.offset + 1}-{Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} customer{pagination.total !== 1 ? 's' : ''}</>
                  )}
                </Typography>
              </Box>
            )}
          </Container>
          <Footer />
        </Box>
      </Box>
      
      <CreateSquarespaceCustomerDialog
        open={createDialogOpen}
        onClose={handleCreateDialogClose}
        formData={createFormData}
        onFormDataChange={setCreateFormData}
        onCreate={handleCreateCustomer}
        creating={creatingCustomer}
        error={createError}
        apiResponse={createApiResponse}
      />

      <OrderSessionDialog
        open={orderSessionDialogOpen}
        onClose={handleOrderSessionDialogClose}
        formData={orderSessionFormData}
        onFormDataChange={setOrderSessionFormData}
        onCreate={handleCreateOrderSession}
        creating={orderSessionCreating}
        error={orderSessionError}
        apiResponse={orderSessionApiResponse}
      />

      <JsonDataModal
        open={jsonModalOpen}
        onClose={() => setJsonModalOpen(false)}
        data={{
          searchQuery: searchQuery,
          filteredResults: customers,
          allResults: customersData,
          pagination: pagination,
        }}
        title="Squarespace Customers JSON - Search Results"
      />

      {SnackbarComponent}
    </>
  );
}
